import React, { forwardRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'

import { AppLoader } from '@components/AppLoader'

import { useLazyFetchViewByIdQuery } from '@redux/api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { useLazyFetchRestrictionsByViewIdQuery } from '@redux/api/restriction.api'

import { transformCommonParametersToValues } from '@helpers'
import { COMMON_PARAMETERS, GLOBAL_PARAMETER_CODE } from '@constants'

import { ConfiguredEntity, ConfiguredEntityProps, GetConfiguredEntityParamsHandle } from './'

export const ConfiguredEntityInitialDataWrapper = forwardRef<
  GetConfiguredEntityParamsHandle,
  ConfiguredEntityProps
>((props, ref) => {
  const { t } = useTranslation()
  const [fetchEntity, { isLoading: isLoadingView, isError: isEntityConfigError }] =
    useLazyFetchViewByIdQuery()
  const { data: globalColumnHeaderStyles, isLoading: isLoadingGlobalHeaderStyles } =
    useFetchParameterPropertiesQuery({
      code: GLOBAL_PARAMETER_CODE.GLOBAL_COLUMN_HEADER_STYLES,
    })
  const { data: globalColumnTextStyles, isLoading: isLoadingGlobalTextStyles } =
    useFetchParameterPropertiesQuery({
      code: GLOBAL_PARAMETER_CODE.GLOBAL_COLUMN_TEXT_STYLES,
    })
  const { data: commonParameters, isLoading: isLoadingGlobalCommonParameters } =
    useFetchParameterPropertiesQuery({
      code: COMMON_PARAMETERS.STATE,
    })
  const [fetchRestrictions, { isLoading: isFetchRestrictionsLoading, data: initialRestrictions }] =
    useLazyFetchRestrictionsByViewIdQuery()

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const handleFetchEntity = () => {
    fetchEntity(props.entityId as string)
      .unwrap()
      .then(entity => props.onSetEntity(entity))
  }

  useEffect(() => {
    if (props.entityId) {
      handleFetchEntity()
      fetchRestrictions({ viewId: props.entityId })
    }
  }, [props.entityId])

  if (
    isLoadingView ||
    isLoadingGlobalCommonParameters ||
    isLoadingGlobalHeaderStyles ||
    isLoadingGlobalTextStyles ||
    isFetchRestrictionsLoading ||
    !props.entity
  ) {
    return <AppLoader />
  }

  if (isEntityConfigError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          width: '100%',
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography color='error' sx={{ fontWeight: 500 }} variant='subtitle1'>
            {t('error.configLoadError')}
          </Typography>
          <Button size='large' variant='contained' onClick={handleFetchEntity}>
            {t('retry')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ConfiguredEntity
      globalColumnHeaderStyles={globalColumnHeaderStyles}
      globalColumnTextStyles={globalColumnTextStyles}
      initialCommonParameters={initialCommonParameters}
      initialRestrictions={initialRestrictions}
      {...props}
      ref={ref}
    />
  )
})

ConfiguredEntityInitialDataWrapper.displayName = 'ConfiguredEntityWrapper'
